import React, { useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { Link } from "react-router-dom";
import { ShieldAlert, ArrowLeft, Lock, Unlock, Monitor, Headset, ScrollText, RefreshCw, SlidersHorizontal, Save, Download, FileText, Ban, BookOpen, Users, LogOut, Send } from "lucide-react";

const fmt = (ts) => { try { return ts ? new Date(ts).toLocaleString() : "—"; } catch { return ts || "—"; } };

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl glass p-4" data-testid={`sec-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-xs opacity-60"><Icon size={14} /> {label}</div>
      <div className={`font-head text-2xl mt-1 ${tone || ""}`}>{value}</div>
    </div>
  );
}

function BlockForm({ onAdd }) {
  const [kind, setKind] = useState("ip");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const submit = () => { onAdd(kind, value.trim(), reason.trim()); setValue(""); setReason(""); };
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-xl glass p-3" data-testid="sec-block-form">
      <label className="text-xs">Block a<br />
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="block-kind"
          className="mt-1 rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1.5 text-sm">
          <option value="ip">IP address</option>
          <option value="account">Account (username/email)</option>
        </select>
      </label>
      <input value={value} onChange={(e) => setValue(e.target.value)} data-testid="block-value" placeholder={kind === "ip" ? "e.g. 203.0.113.5" : "username or email"}
        className="flex-1 min-w-[180px] rounded-md border border-black/15 dark:border-white/15 bg-transparent px-3 py-1.5 text-sm" />
      <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="block-reason" placeholder="reason (optional)"
        className="flex-1 min-w-[160px] rounded-md border border-black/15 dark:border-white/15 bg-transparent px-3 py-1.5 text-sm" />
      <button onClick={submit} disabled={!value.trim()} data-testid="block-submit"
        className="flex items-center gap-1 rounded-md bg-[#DA1E28] px-3 py-1.5 text-sm text-white lift"><Ban size={14} /> Block</button>
    </div>
  );
}

export default function Security() {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [expDays, setExpDays] = useState(30);
  const [runners, setRunners] = useState(null);

  const load = () => client.get("/admin/security/activity").then((r) => setData(r.data)).catch((e) => setMsg(apiErr(e)));
  const loadCfg = () => client.get("/admin/security/config").then((r) => setCfg(r.data)).catch(() => {});
  const loadRunners = () => client.get("/admin/security/runners").then((r) => setRunners(r.data)).catch(() => {});
  useEffect(() => { load(); loadCfg(); loadRunners(); }, []);

  const unlock = async (identifier) => {
    if (!window.confirm("Release this lockout? The account will be able to sign in again immediately.")) return;
    setBusy(true);
    try { await client.post("/admin/security/unlock", { identifier }); setMsg("Lockout released."); await load(); loadRunners(); }
    catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };

  const setLk = (k, v) => setCfg((c) => ({ ...c, lockout: { ...c.lockout, [k]: v } }));
  const setCh = (ev, chan, v) => setCfg((c) => ({ ...c, channels: { ...c.channels, [ev]: { ...c.channels[ev], [chan]: v } } }));
  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const { data: out } = await client.put("/admin/security/config", {
        max_attempts: Number(cfg.lockout.max_attempts),
        lockout_minutes: Number(cfg.lockout.lockout_minutes),
        window_minutes: Number(cfg.lockout.window_minutes),
        channels: cfg.channels,
        digest: cfg.digest,
      });
      setCfg(out); setMsg("Security settings saved.");
    } catch (e) { setMsg(apiErr(e)); } finally { setSavingCfg(false); }
  };

  if (!data) return <div className="p-8" data-testid="sec-loading">Loading security activity…</div>;
  const c = data.counts || {};

  const download = async (fmt) => {
    try {
      const r = await client.get(`/admin/security/export?fmt=${fmt}&days=${expDays}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `security_activity_${expDays}d.${fmt}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { setMsg(apiErr(e)); }
  };

  const blockConn = async (kind, value, reason) => {
    if (!value) return;
    try { await client.post("/admin/security/block", { kind, value, reason: reason || "" }); setMsg(`Blocked ${kind}: ${value}`); await load(); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const unblock = async (id) => {
    try { await client.post("/admin/security/unblock", { id }); setMsg("Block removed."); await load(); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const setDg = (k, v) => setCfg((c) => ({ ...c, digest: { ...c.digest, [k]: v } }));

  const forceSignout = async (r) => {
    if (!window.confirm(`Force sign-out ${r.name || r.email}? This ends all their sessions and revokes trusted devices — they'll need to sign in with MFA again.`)) return;
    try { const { data } = await client.post(`/admin/security/runners/${r.id}/force-signout`); setMsg(`Signed out ${r.name || r.email} (${data.trusted_devices_revoked} device(s) revoked).`); loadRunners(); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const resendInvite = async (r) => {
    try { await client.post(`/admin/runners/${r.id}/invite`, {}); setMsg(`Setup invite resent to ${r.email}.`); }
    catch (e) { setMsg(apiErr(e)); }
  };

  const downloadQuickstart = async () => {
    try {
      const r = await client.get("/admin/security/quickstart.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "admin_security_quickstart.pdf"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { setMsg(apiErr(e)); }
  };

  return (
    <div className="space-y-8 p-2" data-testid="security-page">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/app/enabler" className="flex items-center gap-1 text-sm text-[var(--primary)]" data-testid="sec-back"><ArrowLeft size={15} /> Master Enabler</Link>
        <ShieldAlert size={22} className="text-[var(--primary)]" />
        <h1 className="font-head text-2xl">Security &amp; Sign-in Activity</h1>
        <div className="ms-auto flex items-center gap-2" data-testid="sec-export">
          <select value={expDays} onChange={(e) => setExpDays(Number(e.target.value))} data-testid="sec-export-days"
            className="rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1.5 text-sm">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
          </select>
          <button onClick={() => download("csv")} data-testid="sec-export-csv" className="flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm lift"><Download size={14} /> CSV</button>
          <button onClick={() => download("pdf")} data-testid="sec-export-pdf" className="flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm lift"><FileText size={14} /> PDF</button>
          <button onClick={downloadQuickstart} data-testid="sec-quickstart" className="flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm lift"><BookOpen size={14} /> Quick-Start</button>
          <button onClick={load} data-testid="sec-refresh" className="flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm lift"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>
      {msg && <p className="text-sm opacity-70" data-testid="sec-msg">{msg}</p>}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat icon={Lock} label="Active lockouts" value={c.active_lockouts ?? 0} tone={c.active_lockouts ? "text-[#DA1E28]" : ""} />
        <Stat icon={Monitor} label="New device sign-ins" value={c.new_device_signins_unread ?? 0} tone={c.new_device_signins_unread ? "text-[#FF832B]" : ""} />
        <Stat icon={Headset} label="Active support seats" value={c.active_support_sessions ?? 0} />
      </div>

      {/* Runners security overview */}
      {runners && runners.count > 0 && (
        <section data-testid="sec-runners-section">
          <h2 className="font-head text-lg mb-3 flex items-center gap-2"><Users size={16} /> Runners security overview
            {runners.locked_count > 0 && <span className="text-xs rounded-full bg-[#DA1E28] text-white px-2 py-0.5">{runners.locked_count} locked</span>}
          </h2>
          <div className="overflow-x-auto rounded-xl glass">
            <table className="w-full text-sm" data-testid="sec-runners">
              <thead className="text-start opacity-60"><tr>
                <th className="p-3">Runner</th><th className="p-3">Status</th><th className="p-3">Last sign-in</th>
                <th className="p-3">Last new-device</th><th className="p-3">Recovery set</th><th className="p-3">Last password event</th><th className="p-3"></th>
              </tr></thead>
              <tbody>
                {runners.runners.map((r) => (
                  <tr key={r.id} className="border-t border-black/5 dark:border-white/5" data-testid="sec-runner-row">
                    <td className="p-3"><div className="font-medium">{r.name || r.username || r.email}</div><div className="text-xs opacity-50">{r.email}</div></td>
                    <td className="p-3">
                      {r.disabled ? <span className="text-xs opacity-60">Disabled</span>
                        : r.locked ? <span className="text-xs font-medium text-[#DA1E28]">🔒 Locked out</span>
                        : <span className="text-xs text-[#24A148]">Active</span>}
                    </td>
                    <td className="p-3 opacity-70">{fmt(r.last_login)}</td>
                    <td className="p-3 opacity-70">{fmt(r.last_new_device)}</td>
                    <td className="p-3">{r.recovery_set ? <span className="text-[#24A148]">✓ Email</span> : <span className="text-[#FF832B]">Not set</span>}{r.phone_set ? <span className="opacity-60"> · SMS</span> : ""}</td>
                    <td className="p-3 opacity-70">{r.last_password_event ? `${(r.last_password_event.type || "").replace(/_/g, " ")} · ${fmt(r.last_password_event.at)}` : "—"}</td>
                    <td className="p-3 text-end whitespace-nowrap">
                      <button onClick={() => forceSignout(r)} data-testid="runner-signout-btn"
                        className="inline-flex items-center gap-1 rounded-md border border-[#DA1E28]/40 text-[#DA1E28] px-2 py-1 text-xs lift"><LogOut size={13} /> Sign out</button>
                      <button onClick={() => resendInvite(r)} data-testid="runner-invite-btn"
                        className="ms-2 inline-flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-2 py-1 text-xs lift"><Send size={13} /> Resend invite</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Alert & lockout settings */}
      {cfg && (
        <section className="rounded-xl glass p-4" data-testid="sec-settings">
          <h2 className="font-head text-lg mb-3 flex items-center gap-2"><SlidersHorizontal size={16} /> Alert &amp; lockout settings</h2>
          <div className="grid gap-4 sm:grid-cols-3 mb-4">
            <label className="text-sm">Lockout after (failed attempts)
              <input type="number" min={3} max={20} value={cfg.lockout.max_attempts} data-testid="cfg-max-attempts"
                onChange={(e) => setLk("max_attempts", e.target.value)}
                className="mt-1 w-full rounded-md border border-black/15 dark:border-white/15 bg-transparent px-3 py-1.5" />
            </label>
            <label className="text-sm">Lockout duration (minutes)
              <input type="number" min={1} max={1440} value={cfg.lockout.lockout_minutes} data-testid="cfg-lockout-minutes"
                onChange={(e) => setLk("lockout_minutes", e.target.value)}
                className="mt-1 w-full rounded-md border border-black/15 dark:border-white/15 bg-transparent px-3 py-1.5" />
            </label>
            <label className="text-sm">Attempt window (minutes)
              <input type="number" min={1} max={1440} value={cfg.lockout.window_minutes} data-testid="cfg-window-minutes"
                onChange={(e) => setLk("window_minutes", e.target.value)}
                className="mt-1 w-full rounded-md border border-black/15 dark:border-white/15 bg-transparent px-3 py-1.5" />
            </label>
          </div>
          <div className="rounded-lg border border-black/10 dark:border-white/10 overflow-hidden">
            <table className="w-full text-sm" data-testid="cfg-channels">
              <thead className="text-start opacity-60"><tr>
                <th className="p-3">Alert event</th><th className="p-3 text-center">In-app</th><th className="p-3 text-center">Email</th><th className="p-3 text-center">SMS</th>
              </tr></thead>
              <tbody>
                {(cfg.events || []).map((ev) => (
                  <tr key={ev.key} className="border-t border-black/5 dark:border-white/5">
                    <td className="p-3 font-medium">{ev.label}</td>
                    <td className="p-3 text-center opacity-50" title="Always on">✓</td>
                    <td className="p-3 text-center">
                      <input type="checkbox" checked={!!cfg.channels[ev.key]?.email} data-testid={`cfg-${ev.key}-email`}
                        onChange={(e) => setCh(ev.key, "email", e.target.checked)} />
                    </td>
                    <td className="p-3 text-center">
                      <input type="checkbox" checked={!!cfg.channels[ev.key]?.sms} data-testid={`cfg-${ev.key}-sms`}
                        onChange={(e) => setCh(ev.key, "sms", e.target.checked)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 rounded-lg border border-black/10 dark:border-white/10 p-3" data-testid="sec-digest">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={!!cfg.digest?.enabled} data-testid="digest-enabled"
                onChange={(e) => setDg("enabled", e.target.checked)} />
              Weekly security digest (email summary of the past 7 days)
            </label>
            {cfg.digest?.enabled && (
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <label className="text-sm">Day
                  <select value={cfg.digest.weekday} onChange={(e) => setDg("weekday", Number(e.target.value))} data-testid="digest-weekday"
                    className="mt-1 w-full rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1.5">
                    {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </label>
                <label className="text-sm">Hour (UTC)
                  <input type="number" min={0} max={23} value={cfg.digest.hour} onChange={(e) => setDg("hour", Number(e.target.value))} data-testid="digest-hour"
                    className="mt-1 w-full rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1.5" />
                </label>
                <label className="flex items-end gap-2 text-sm pb-1.5">
                  <input type="checkbox" checked={!!cfg.digest.sms} onChange={(e) => setDg("sms", e.target.checked)} data-testid="digest-sms" />
                  Also send SMS summary
                </label>
              </div>
            )}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button onClick={saveCfg} disabled={savingCfg} data-testid="cfg-save"
              className="flex items-center gap-1 rounded-md bg-[var(--primary)] px-4 py-1.5 text-sm text-white lift"><Save size={14} /> {savingCfg ? "Saving…" : "Save settings"}</button>
            <span className="text-xs opacity-50">In-app alerts are always recorded; email/SMS go to your recovery contacts.</span>
          </div>
        </section>
      )}

      {/* Active lockouts */}
      <section>
        <h2 className="font-head text-lg mb-3 flex items-center gap-2"><Lock size={16} /> Active brute-force lockouts</h2>
        <div className="overflow-x-auto rounded-xl glass">
          <table className="w-full text-sm" data-testid="sec-lockouts">
            <thead className="text-start opacity-60"><tr>
              <th className="p-3">Type</th><th className="p-3">Account</th><th className="p-3">IP</th><th className="p-3">Locked until</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {data.active_lockouts.length === 0 && <tr><td colSpan={5} className="p-3 opacity-60">No accounts are currently locked out. 🎉</td></tr>}
              {data.active_lockouts.map((l) => (
                <tr key={l.identifier} className="border-t border-black/5 dark:border-white/5" data-testid="sec-lockout-row">
                  <td className="p-3 capitalize">{l.kind}</td>
                  <td className="p-3 font-medium">{l.account}</td>
                  <td className="p-3 opacity-70">{l.ip || "—"}</td>
                  <td className="p-3 opacity-70">{fmt(l.locked_until)}</td>
                  <td className="p-3 text-end whitespace-nowrap">
                    <button onClick={() => unlock(l.identifier)} disabled={busy} data-testid="sec-unlock-btn"
                      className="inline-flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-2 py-1 text-xs lift"><Unlock size={13} /> Release</button>
                    {l.ip && <button onClick={() => blockConn("ip", l.ip, "Blocked from lockout list")} data-testid="sec-block-ip-btn"
                      className="ms-2 inline-flex items-center gap-1 rounded-md border border-[#DA1E28]/40 text-[#DA1E28] px-2 py-1 text-xs lift"><Ban size={13} /> Block IP</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Blocked connections */}
      <section data-testid="sec-blocks-section">
        <h2 className="font-head text-lg mb-3 flex items-center gap-2"><Ban size={16} /> Blocked connections</h2>
        <BlockForm onAdd={blockConn} />
        <div className="overflow-x-auto rounded-xl glass mt-3">
          <table className="w-full text-sm" data-testid="sec-blocks">
            <thead className="text-start opacity-60"><tr>
              <th className="p-3">Type</th><th className="p-3">Value</th><th className="p-3">Reason</th><th className="p-3">Added</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {(!data.blocks || data.blocks.length === 0) && <tr><td colSpan={5} className="p-3 opacity-60">No connections are blocked.</td></tr>}
              {(data.blocks || []).map((b) => (
                <tr key={b.id} className="border-t border-black/5 dark:border-white/5" data-testid="sec-block-row">
                  <td className="p-3 uppercase text-xs opacity-70">{b.kind}</td>
                  <td className="p-3 font-medium">{b.value}</td>
                  <td className="p-3 opacity-70">{b.reason || "—"}</td>
                  <td className="p-3 opacity-70">{fmt(b.created_at)}</td>
                  <td className="p-3 text-end">
                    <button onClick={() => unblock(b.id)} data-testid="sec-unblock-btn"
                      className="inline-flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-2 py-1 text-xs lift"><Unlock size={13} /> Unblock</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* New device sign-ins */}
      <section>
        <h2 className="font-head text-lg mb-3 flex items-center gap-2"><Monitor size={16} /> Recent new-device sign-ins</h2>
        <div className="space-y-2" data-testid="sec-new-devices">
          {data.new_device_signins.length === 0 && <p className="text-sm opacity-60">No new-device sign-ins recorded.</p>}
          {data.new_device_signins.map((a) => (
            <div key={a.id} className="rounded-lg glass p-3" data-testid="sec-device-row">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm">{a.title}</span>
                <span className="text-xs opacity-50">{fmt(a.created_at)}</span>
              </div>
              <p className="text-xs opacity-70 mt-1">{a.message}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Active support sessions */}
      <section>
        <h2 className="font-head text-lg mb-3 flex items-center gap-2"><Headset size={16} /> Active support sessions</h2>
        <div className="overflow-x-auto rounded-xl glass">
          <table className="w-full text-sm" data-testid="sec-sessions">
            <thead className="text-start opacity-60"><tr>
              <th className="p-3">Support user</th><th className="p-3">Language</th><th className="p-3">Started</th><th className="p-3">Last seen</th>
            </tr></thead>
            <tbody>
              {data.active_support_sessions.length === 0 && <tr><td colSpan={4} className="p-3 opacity-60">No support staff are signed in right now.</td></tr>}
              {data.active_support_sessions.map((s) => (
                <tr key={s.id} className="border-t border-black/5 dark:border-white/5" data-testid="sec-session-row">
                  <td className="p-3 font-medium">{s.support_username || s.support_user_id}</td>
                  <td className="p-3 opacity-70">{s.language || "—"}</td>
                  <td className="p-3 opacity-70">{fmt(s.created_at)}</td>
                  <td className="p-3 opacity-70">{fmt(s.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Recent security events */}
      <section>
        <h2 className="font-head text-lg mb-3 flex items-center gap-2"><ScrollText size={16} /> Recent sign-in &amp; security events</h2>
        <div className="overflow-x-auto rounded-xl glass">
          <table className="w-full text-sm" data-testid="sec-events">
            <thead className="text-start opacity-60"><tr>
              <th className="p-3">Who</th><th className="p-3">Event</th><th className="p-3">Detail</th><th className="p-3">When</th>
            </tr></thead>
            <tbody>
              {data.recent_events.length === 0 && <tr><td colSpan={4} className="p-3 opacity-60">No security events recorded yet.</td></tr>}
              {data.recent_events.map((e, i) => (
                <tr key={i} className="border-t border-black/5 dark:border-white/5" data-testid="sec-event-row">
                  <td className="p-3 font-medium">{e.who}</td>
                  <td className="p-3 capitalize">{(e.type || "").replace(/_/g, " ")}</td>
                  <td className="p-3 opacity-70">{e.detail || "—"}</td>
                  <td className="p-3 opacity-70">{fmt(e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
